import { __rest, __assign } from '../../../node_modules/tslib/tslib.es6.js';
import React__default__default, { forwardRef, useRef, useContext, useLayoutEffect } from 'react';
import PropTypes__default from 'prop-types';
import ReactUtils from '../../../packages/react-utils/react-utils.js';
import { SubMenuContext, SubMenuInitialFocus } from '../../../contexts/sub-menu-context.js';
import { requiredChildrenValidation } from '../../../prop-types-validators/children-validator.js';

var SubMenu = /*#__PURE__*/forwardRef(function (_a, ref) {
  var className = _a.className,
    children = _a.children,
    rest = __rest(_a, ["className", "children"]);
  var firstItemRef = useRef();
  var lastItemRef = useRef();
  var subMenuContextValue = useContext(SubMenuContext);
  var setInitialFocus = subMenuContextValue.setInitialFocus,
    hidden = subMenuContextValue.hidden,
    onEscapeCallback = subMenuContextValue.onEscapeCallback,
    target = subMenuContextValue.target;
  var handleMenuKeyDown = function (event) {
    if (event.key === 'Escape' && onEscapeCallback) {
      onEscapeCallback(event);
    }
    if (rest.onKeyDown) {
      rest.onKeyDown(event);
    }
  };
  var handleItemKeyDown = function (event) {
    var _a, _b, _c, _d, _e, _f;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      // cycles back to first item when is the last
      if (event.target === lastItemRef.current) {
        (_a = firstItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      } else {
        // or sets focus to next menu item
        var eventTargetElem = event.target;
        var nextLink = (_c = (_b = eventTargetElem.parentElement) === null || _b === void 0 ? void 0 : _b.nextSibling) === null || _c === void 0 ? void 0 : _c.firstChild;
        nextLink === null || nextLink === void 0 ? void 0 : nextLink.focus();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      // cycles back to last link when is the first
      if (event.target === firstItemRef.current) {
        (_d = lastItemRef.current) === null || _d === void 0 ? void 0 : _d.focus();
      } else {
        // or sets focus to previous menu item
        var eventTargetElem = event.target;
        var prevLink = (_f = (_e = eventTargetElem.parentElement) === null || _e === void 0 ? void 0 : _e.previousSibling) === null || _f === void 0 ? void 0 : _f.firstChild;
        prevLink === null || prevLink === void 0 ? void 0 : prevLink.focus();
      }
    }
  };
  useLayoutEffect(function () {
    var _a, _b;
    switch (setInitialFocus) {
      case SubMenuInitialFocus.Target:
        target === null || target === void 0 ? void 0 : target.focus();
        break;
      case SubMenuInitialFocus.First:
        (_a = firstItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        break;
      case SubMenuInitialFocus.Last:
        (_b = lastItemRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        break;
    }
  }, [hidden, setInitialFocus]);
  var childrenArray = React__default__default.Children.toArray(children);
  var enhancedItems = childrenArray.map(function (child, i) {
    var isFirstItem = i === 0;
    var isLastItem = i === childrenArray.length - 1;
    if (ReactUtils.isOfType(child, 'NavItem') || ReactUtils.isOfType(child, 'DropdownItem')) {
      if (ReactUtils.isOfType(child, 'NavItem')) {
        return /*#__PURE__*/React__default__default.cloneElement(child, __assign(__assign({
          onKeyDown: handleItemKeyDown,
          role: 'presentation',
          linkRole: 'menuitem'
        }, isFirstItem && {
          linkRef: firstItemRef
        }), isLastItem && {
          linkRef: lastItemRef
        }));
      }
      if (ReactUtils.isOfType(child, 'DropdownItem')) {
        return /*#__PURE__*/React__default__default.cloneElement(child, __assign(__assign({
          onKeyDown: handleItemKeyDown
        }, isFirstItem && {
          linkRef: firstItemRef
        }), isLastItem && {
          linkRef: lastItemRef
        }));
      }
    }
    return child;
  });
  return /*#__PURE__*/React__default__default.createElement("ul", __assign({
    ref: ref,
    role: "menu",
    className: className,
    hidden: hidden
  }, rest, {
    onKeyDown: handleMenuKeyDown
  }), enhancedItems);
});
SubMenu.displayName = 'SubMenu';
SubMenu.propTypes = {
  className: PropTypes__default.string,
  // eslint-disable-next-line react/require-default-props
  children: requiredChildrenValidation(['NavItem', 'DropdownItem'])
};

export { SubMenu, SubMenu as default };
